import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertOctagon,
  IndianRupee,
  RotateCcw,
  Save,
  Loader2,
  Sparkles,
  Layers,
  Plus,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { penaltyService } from '@/services/penalty.service';
import type { LateArrivalTierRule } from '@pms/shared';
import type { TierRuleInput } from '@pms/shared';

type RateMode = 'per_hour' | 'global_rate' | 'multiplier' | 'fixed_amount';

interface ConfigState {
  miss_punch_minutes:         number;
  late_arrival_min_threshold: number;
  exclude_sundays_late_arrival: boolean;
  exclude_sundays_miss_punch:   boolean;
  auto_generate_enabled:      boolean;
  penalty_gap_days:           number;
  late_leave_correction_enabled: boolean;
  penalty_rate_mode:          RateMode;
  penalty_rate_per_hour:      number | null;
  penalty_rate_multiplier:    number;
  penalty_fixed_amount:       number | null;
}

const DEFAULTS: ConfigState = {
  miss_punch_minutes:         60,
  late_arrival_min_threshold: 0,
  exclude_sundays_late_arrival: true,
  exclude_sundays_miss_punch:   true,
  auto_generate_enabled:      true,
  penalty_gap_days:           1,
  late_leave_correction_enabled: true,
  penalty_rate_mode:          'per_hour',
  penalty_rate_per_hour:      null,
  penalty_rate_multiplier:    1.0,
  penalty_fixed_amount:       null,
};

const RATE_MODES: { value: RateMode; label: string; desc: string }[] = [
  { value: 'per_hour',     label: 'Per Hour Rate',     desc: "Uses each employee's own salary rate. Fair across all levels." },
  { value: 'global_rate',  label: 'Global Fixed Rate', desc: 'Same ₹/hr for everyone, ignoring their salary.' },
  { value: 'multiplier',   label: 'Multiplier',        desc: "Employee's rate × a factor. e.g. 1.5× = 50% extra." },
  { value: 'fixed_amount', label: 'Fixed Amount',      desc: 'Flat ₹ per penalty event, no matter the hours.' },
];

// Example employee used for the live preview
const EX_RATE = 100;

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!);
}

// Build the next occurrence_number to use when adding a new tier
function nextOccurrence(tiers: TierRuleInput[]): number {
  const specific = tiers.filter((r) => r.occurrence_number > 0);
  if (specific.length === 0) return 1;
  return Math.max(...specific.map((r) => r.occurrence_number)) + 1;
}

// Convert server rules to local editable state (sort: specific asc, default last)
function toLocal(rules: LateArrivalTierRule[]): TierRuleInput[] {
  return [...rules]
    .sort((a, b) => {
      if (a.occurrence_number === 0) return 1;
      if (b.occurrence_number === 0) return -1;
      return a.occurrence_number - b.occurrence_number;
    })
    .map((r) => ({
      occurrence_number: r.occurrence_number,
      penalty_minutes:   r.penalty_minutes,
      label:             r.label ?? '',
    }));
}

export function PenaltyConfigSection() {
  const queryClient = useQueryClient();
  const [local, setLocal] = useState<ConfigState>(DEFAULTS);
  const [localTiers, setLocalTiers] = useState<TierRuleInput[]>([]);

  const { data: config, isLoading } = useQuery({
    queryKey: ['penalty', 'config'],
    queryFn: () => penaltyService.getConfig(),
  });

  const { data: tierData, isLoading: tiersLoading } = useQuery({
    queryKey: ['penalty', 'tier-rules'],
    queryFn: () => penaltyService.getTierRules(),
  });

  useEffect(() => {
    if (config) {
      setLocal({
        miss_punch_minutes:         config.miss_punch_minutes,
        late_arrival_min_threshold: config.late_arrival_min_threshold,
        exclude_sundays_late_arrival: config.exclude_sundays_late_arrival,
        exclude_sundays_miss_punch:   config.exclude_sundays_miss_punch,
        auto_generate_enabled:      config.auto_generate_enabled,
        penalty_gap_days:           config.penalty_gap_days,
        late_leave_correction_enabled: config.late_leave_correction_enabled,
        penalty_rate_mode:          config.penalty_rate_mode,
        penalty_rate_per_hour:      config.penalty_rate_per_hour ? parseFloat(config.penalty_rate_per_hour) : null,
        penalty_rate_multiplier:    parseFloat(config.penalty_rate_multiplier),
        penalty_fixed_amount:       config.penalty_fixed_amount ? parseFloat(config.penalty_fixed_amount) : null,
      });
    }
  }, [config]);

  useEffect(() => {
    if (tierData) {
      const rows = toLocal(tierData);
      // Always ensure a Default row exists (occurrence_number = 0)
      if (!rows.find((r) => r.occurrence_number === 0)) {
        rows.push({ occurrence_number: 0, penalty_minutes: 120, label: '4th+ Late – 2 hr deduction' });
      }
      setLocalTiers(rows);
    }
  }, [tierData]);

  const save = useMutation({
    mutationFn: () => penaltyService.updateConfig({
      miss_punch_minutes:         local.miss_punch_minutes,
      late_arrival_min_threshold: local.late_arrival_min_threshold,
      exclude_sundays_late_arrival: local.exclude_sundays_late_arrival,
      exclude_sundays_miss_punch:   local.exclude_sundays_miss_punch,
      auto_generate_enabled:      local.auto_generate_enabled,
      penalty_gap_days:           local.penalty_gap_days,
      late_leave_correction_enabled: local.late_leave_correction_enabled,
      penalty_rate_mode:          local.penalty_rate_mode,
      penalty_rate_per_hour:      local.penalty_rate_per_hour,
      penalty_rate_multiplier:    local.penalty_rate_multiplier,
      penalty_fixed_amount:       local.penalty_fixed_amount,
    }),
    onSuccess: () => {
      toast.success('Penalty config saved');
      void queryClient.invalidateQueries({ queryKey: ['penalty', 'config'] });
    },
    onError: () => toast.error('Failed to save config'),
  });

  const saveTiers = useMutation({
    mutationFn: () => penaltyService.upsertTierRules(localTiers),
    onSuccess: () => {
      toast.success('Late arrival tiers saved');
      void queryClient.invalidateQueries({ queryKey: ['penalty', 'tier-rules'] });
    },
    onError: () => toast.error('Failed to save tiers'),
  });

  const set = <K extends keyof ConfigState>(key: K, value: ConfigState[K]) =>
    setLocal((prev) => ({ ...prev, [key]: value }));

  const reset = () => setLocal(DEFAULTS);

  const updateTier = (idx: number, field: keyof TierRuleInput, value: string | number) =>
    setLocalTiers((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    );

  const addTier = () => {
    const next = nextOccurrence(localTiers);
    setLocalTiers((prev) => {
      // Insert before the default (occurrence_number === 0) row
      const defaultIdx = prev.findIndex((r) => r.occurrence_number === 0);
      const newRow: TierRuleInput = { occurrence_number: next, penalty_minutes: 0, label: `${ordinal(next)} Late` };
      if (defaultIdx === -1) return [...prev, newRow];
      const copy = [...prev];
      copy.splice(defaultIdx, 0, newRow);
      return copy;
    });
  };

  const removeTier = (idx: number) =>
    setLocalTiers((prev) => prev.filter((_, i) => i !== idx));

  if (isLoading || tiersLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading config…
      </div>
    );
  }

  // ── Live preview: what one missed punch costs an employee at ₹100/hr ──
  const missH = local.miss_punch_minutes / 60;
  let exampleDeduction = 0;
  let rateExplain = '';
  switch (local.penalty_rate_mode) {
    case 'per_hour':
      exampleDeduction = missH * EX_RATE;
      rateExplain = `their own rate (₹${EX_RATE}/hr)`;
      break;
    case 'global_rate':
      exampleDeduction = missH * (local.penalty_rate_per_hour ?? 0);
      rateExplain = `the global rate (₹${local.penalty_rate_per_hour ?? 0}/hr)`;
      break;
    case 'multiplier':
      exampleDeduction = missH * EX_RATE * local.penalty_rate_multiplier;
      rateExplain = `₹${EX_RATE}/hr × ${local.penalty_rate_multiplier}`;
      break;
    case 'fixed_amount':
      exampleDeduction = local.penalty_fixed_amount ?? 0;
      rateExplain = 'a flat fee per event';
      break;
  }

  // Tier preview text
  const specificTiers = localTiers.filter((r) => r.occurrence_number > 0);
  const defaultTier   = localTiers.find((r) => r.occurrence_number === 0);
  const tierPreview = specificTiers.length > 0
    ? specificTiers.map((r) => `${ordinal(r.occurrence_number)}: ${r.penalty_minutes}min`).join(', ')
      + (defaultTier ? `, default: ${defaultTier.penalty_minutes}min` : '')
    : defaultTier
      ? `default: ${defaultTier.penalty_minutes}min`
      : '';

  return (
    <div className="space-y-4">

      {/* Live summary strip */}
      <div className="flex items-start gap-2.5 rounded-lg border border-border/60 bg-muted/30 px-4 py-3 text-sm">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p className="text-foreground">
          A missed punch = <strong>{missH.toFixed(1)}h</strong> penalty.{' '}
          {local.penalty_rate_mode === 'fixed_amount' ? (
            <>Each penalty deducts <strong>₹{(local.penalty_fixed_amount ?? 0).toLocaleString('en-IN')}</strong> ({rateExplain}).</>
          ) : (
            <>An employee penalized at {rateExplain} loses <strong>₹{exampleDeduction.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong> for it.</>
          )}
          {tierPreview && (
            <> Late arrival tiers: <strong>{tierPreview}</strong>.</>
          )}
        </p>
      </div>

      {/* Card 1 — Triggers */}
      <div className="rounded-lg border border-border/60 bg-card">
        <div className="flex items-center gap-2 border-b border-border/40 px-4 py-3">
          <AlertOctagon className="h-4 w-4 text-rose-600" />
          <div>
            <h3 className="text-sm font-semibold text-foreground">When is a penalty applied?</h3>
            <p className="text-xs text-muted-foreground">Rules that auto-create penalties from attendance.</p>
          </div>
        </div>

        <div className="divide-y divide-border/40">
          {/* Miss punch */}
          <SettingRow
            title="Miss Punch Penalty"
            desc="When an employee has no attendance record and isn't on approved leave."
          >
            <div className="flex items-center gap-2">
              <Input
                type="number" min={1} max={480} step={15}
                value={local.miss_punch_minutes}
                onChange={(e) => set('miss_punch_minutes', Math.max(1, parseInt(e.target.value) || 1))}
                className="h-9 w-24 text-right"
              />
              <span className="text-xs text-muted-foreground">min&nbsp;=&nbsp;{(local.miss_punch_minutes / 60).toFixed(1)}h</span>
            </div>
          </SettingRow>

          {/* Late arrival grace */}
          <SettingRow
            title="Late Arrival Grace"
            desc={local.late_arrival_min_threshold === 0
              ? 'Any lateness is penalized (no grace).'
              : `Arrivals ≤ ${local.late_arrival_min_threshold} min late are forgiven.`}
          >
            <div className="flex items-center gap-2">
              <Input
                type="number" min={0} max={120} step={5}
                value={local.late_arrival_min_threshold}
                onChange={(e) => set('late_arrival_min_threshold', Math.max(0, parseInt(e.target.value) || 0))}
                className="h-9 w-24 text-right"
              />
              <span className="text-xs text-muted-foreground">min</span>
            </div>
          </SettingRow>

          {/* Waiting period before a penalty is created */}
          <SettingRow
            title="Penalty Waiting Period"
            desc={local.penalty_gap_days === 0
              ? 'Penalties are created as soon as the day is over. Never same-day.'
              : `Penalties are held for ${local.penalty_gap_days} complete ${local.penalty_gap_days === 1 ? 'day' : 'days'} so a short leave or half-day leave has time to be approved first. If the leave is approved, no penalty is created; if it is still unapproved or rejected after the wait, the penalty is created.`}
          >
            <div className="flex items-center gap-2">
              <Input
                type="number" min={0} max={30} step={1}
                value={local.penalty_gap_days}
                onChange={(e) => set('penalty_gap_days', Math.min(30, Math.max(0, parseInt(e.target.value) || 0)))}
                className="h-9 w-24 text-right"
              />
              <span className="text-xs text-muted-foreground">{local.penalty_gap_days === 1 ? 'day' : 'days'}</span>
            </div>
          </SettingRow>

          {/* Toggles */}
          <ToggleRow
            title="Exclude Sundays — Late Arrival"
            desc="Skip Sunday when auto-generating late arrival penalties."
            checked={local.exclude_sundays_late_arrival}
            onChange={(v) => set('exclude_sundays_late_arrival', v)}
          />
          <ToggleRow
            title="Exclude Sundays — Miss Punch"
            desc="Skip Sunday when auto-generating miss punch penalties."
            checked={local.exclude_sundays_miss_punch}
            onChange={(v) => set('exclude_sundays_miss_punch', v)}
          />
          <ToggleRow
            title="Correct late penalty when leave is approved later"
            desc={local.late_leave_correction_enabled
              ? "A short leave, morning half-day or full-day leave approved for a past day removes or reduces that day's late penalty — even if already approved — and later late penalties that month are re-ranked."
              : "Once a late penalty is created, a leave approved afterwards does not change it."}
            checked={local.late_leave_correction_enabled}
            onChange={(v) => set('late_leave_correction_enabled', v)}
          />
          <ToggleRow
            title="Nightly Auto-Generation"
            desc="Automatically create penalties every night at 00:30."
            checked={local.auto_generate_enabled}
            onChange={(v) => set('auto_generate_enabled', v)}
          />
        </div>
      </div>

      {/* Card 2 — Rate mode */}
      <div className="rounded-lg border border-border/60 bg-card">
        <div className="flex items-center gap-2 border-b border-border/40 px-4 py-3">
          <IndianRupee className="h-4 w-4 text-primary" />
          <div>
            <h3 className="text-sm font-semibold text-foreground">How much money is deducted?</h3>
            <p className="text-xs text-muted-foreground">Converts penalty minutes into a salary deduction.</p>
          </div>
        </div>

        <div className="space-y-3 px-4 py-4">
          {/* Mode grid */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {RATE_MODES.map((m) => {
              const active = local.penalty_rate_mode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => set('penalty_rate_mode', m.value)}
                  className={`flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors ${
                    active ? 'border-primary bg-primary/5' : 'border-border/60 hover:border-border'
                  }`}
                >
                  <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                    active ? 'border-primary' : 'border-muted-foreground/40'
                  }`}>
                    {active && <span className="h-2 w-2 rounded-full bg-primary" />}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground">{m.label}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{m.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Mode-specific sub-panel */}
          <div className="rounded-lg bg-muted/40 px-4 py-3">
            {local.penalty_rate_mode === 'per_hour' && (
              <p className="text-sm text-muted-foreground">
                No extra setup — each employee's own per-hour salary rate is used.
              </p>
            )}

            {local.penalty_rate_mode === 'global_rate' && (
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm font-medium text-foreground">Global Rate</label>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground">₹</span>
                  <Input
                    type="number" min={1} step={1}
                    value={local.penalty_rate_per_hour ?? ''}
                    onChange={(e) => set('penalty_rate_per_hour', parseFloat(e.target.value) || null)}
                    placeholder="20"
                    className="h-9 w-28"
                  />
                  <span className="text-sm text-muted-foreground">/hr</span>
                </div>
              </div>
            )}

            {local.penalty_rate_mode === 'multiplier' && (
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm font-medium text-foreground">Multiplier</label>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number" min={0.1} max={10} step={0.1}
                    value={local.penalty_rate_multiplier}
                    onChange={(e) => set('penalty_rate_multiplier', parseFloat(e.target.value) || 1.0)}
                    className="h-9 w-24 text-right"
                  />
                  <span className="text-sm text-muted-foreground">×</span>
                </div>
              </div>
            )}

            {local.penalty_rate_mode === 'fixed_amount' && (
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm font-medium text-foreground">Amount per Event</label>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground">₹</span>
                  <Input
                    type="number" min={1} step={50}
                    value={local.penalty_fixed_amount ?? ''}
                    onChange={(e) => set('penalty_fixed_amount', parseFloat(e.target.value) || null)}
                    placeholder="500"
                    className="h-9 w-28"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer — save main config */}
      <div className="flex items-center gap-3 pt-1">
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {save.isPending ? 'Saving…' : 'Save Changes'}
        </Button>
        <button
          type="button"
          onClick={reset}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset to defaults
        </button>
      </div>

      {/* Card 3 — Late Arrival Tiers */}
      <div className="rounded-lg border border-border/60 bg-card">
        <div className="flex items-center gap-2 border-b border-border/40 px-4 py-3">
          <Layers className="h-4 w-4 text-violet-600" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-foreground">Late Arrival Tiers</h3>
            <p className="text-xs text-muted-foreground">
              Dynamic penalty based on how many times an employee has been late this calendar month.
              Occurrences reset on the 1st of each month.
            </p>
          </div>
        </div>

        <div className="px-4 py-4 space-y-3">
          {/* Header row */}
          <div className="grid grid-cols-[80px_1fr_120px_36px] gap-2 px-1">
            <span className="text-xs font-medium text-muted-foreground">Occurrence</span>
            <span className="text-xs font-medium text-muted-foreground">Label</span>
            <span className="text-xs font-medium text-muted-foreground text-right">Penalty (min)</span>
            <span />
          </div>

          {/* Tier rows */}
          <div className="space-y-2">
            {localTiers.map((tier, idx) => {
              const isDefault = tier.occurrence_number === 0;
              return (
                <div key={isDefault ? 'default' : tier.occurrence_number} className={`grid grid-cols-[80px_1fr_120px_36px] gap-2 items-center rounded-lg px-1 py-1 ${isDefault ? 'bg-muted/40' : ''}`}>
                  {/* Occurrence label */}
                  <span className={`text-xs font-semibold ${isDefault ? 'text-muted-foreground' : 'text-foreground'}`}>
                    {isDefault ? 'Default' : ordinal(tier.occurrence_number)}
                  </span>

                  {/* Label input */}
                  <Input
                    type="text"
                    value={tier.label ?? ''}
                    onChange={(e) => updateTier(idx, 'label', e.target.value)}
                    placeholder={isDefault ? 'e.g. Repeated Late' : `e.g. ${ordinal(tier.occurrence_number)} Late`}
                    className="h-8 text-sm"
                  />

                  {/* Minutes input */}
                  <div className="flex items-center justify-end gap-1">
                    <Input
                      type="number"
                      min={0}
                      max={1440}
                      step={15}
                      value={tier.penalty_minutes}
                      onChange={(e) => updateTier(idx, 'penalty_minutes', Math.max(0, parseInt(e.target.value) || 0))}
                      className="h-8 w-20 text-right text-sm"
                    />
                    <span className="text-xs text-muted-foreground shrink-0">min</span>
                  </div>

                  {/* Delete button — not allowed for default row */}
                  {isDefault ? (
                    <span />
                  ) : (
                    <button
                      type="button"
                      onClick={() => removeTier(idx)}
                      className="flex h-8 w-8 items-center justify-center rounded text-muted-foreground hover:bg-rose-50 hover:text-rose-600 transition-colors"
                      title="Remove tier"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add tier button */}
          <button
            type="button"
            onClick={addTier}
            className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors pt-1"
          >
            <Plus className="h-4 w-4" />
            Add occurrence tier
          </button>

          <p className="text-xs text-muted-foreground pt-1">
            <strong>0 min</strong> = warning only (recorded but no salary deduction).
            The <strong>Default</strong> row applies to any occurrence beyond the defined tiers.
          </p>
        </div>

        {/* Tier save footer */}
        <div className="flex items-center gap-3 border-t border-border/40 px-4 py-3">
          <Button
            size="sm"
            onClick={() => saveTiers.mutate()}
            disabled={saveTiers.isPending}
          >
            {saveTiers.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saveTiers.isPending ? 'Saving…' : 'Save Tiers'}
          </Button>
          {tierData && (
            <button
              type="button"
              onClick={() => setLocalTiers(toLocal(tierData))}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Discard changes
            </button>
          )}
        </div>
      </div>

    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function SettingRow({
  title, desc, children,
}: {
  title: string; desc: string; children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      {children}
    </div>
  );
}

function ToggleRow({
  title, desc, checked, onChange,
}: {
  title: string; desc: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer select-none items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      <Switch checked={checked} onChange={onChange} size="sm" aria-label={title} />
    </label>
  );
}
